import React, { useEffect, useState } from 'react';

import {
  Button,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle
} from '@material-ui/core';
import { Alert } from '@material-ui/lab';

import { ApiParams, FetchError } from '../../../common/api/api';
import SelectDialog, {
  SelectDialogSearchField,
  SelectDialogSubmitButton,
  SelectDialogTable
} from '../../../common/dialogs/SelectDialog';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { useFindFilesPaginated } from '../Document.hook';
import { FileDto } from '../Document.interface';

export interface AssignDocumentDialogProps {
  open: boolean;
  title?: string;
  selectedItems?: FileDto[];
  contentText?: string;
  isLoading?: boolean;
  defaultLimit?: number;
  onCancel?: () => void;
  onError?: (error: FetchError) => void;
  onSubmit?: (files: FileDto[]) => void;
}

const AssignDocumentDialog: React.FC<AssignDocumentDialogProps> = (
  props: AssignDocumentDialogProps
) => {
  const {
    open,
    selectedItems,
    defaultLimit = 5,
    isLoading: isLoadingProp = false,
    title = 'Assign Document',
    contentText = 'Search for Document to assign.',
    onCancel,
    onError,
    onSubmit
  } = props;
  const [page, setPage] = useState<number>(0);
  const [limit, setLimit] = useState<number>(defaultLimit);
  const [search, setSearch] = useState<string>('');
  const [{ data, isLoading: isFinding, error }, findFiles] =
    useFindFilesPaginated();
  const isLoading: boolean = isLoadingProp || isFinding;

  // handle searching
  useEffect(() => {
    const params: ApiParams = { page, limit, search };
    findFiles(params);
  }, [page, limit, search, findFiles]);

  useEffect(() => {
    if (error) {
      onError?.(error);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [error]);

  return (
    <SelectDialog open={open} onClose={onCancel} selectedItems={selectedItems}>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <DialogContentText>{contentText}</DialogContentText>
        <SelectDialogSearchField
          label='Document'
          onSearch={(value: string) => {
            setPage(0);
            setSearch(value);
          }}
        />
        <SelectDialogTable
          multiple
          idKey='id'
          columns={[
            {
              key: 'id',
              label: 'ID',
              render: (item: FileDto) => item.task?.key || `DOC-${item.id}`
            },
            { key: 'name', label: 'Name', wrap: true },
            {
              key: 'version',
              label: 'Version',
              render: (item: FileDto) => {
                const version =
                  item.task?.versionLog?.version ||
                  item.task?.version ||
                  item.version ||
                  1;
                return <>{version}</>;
              }
            },
            {
              key: 'task.status',
              label: 'Status',
              render: (item: FileDto) => {
                return item.task && <TaskStatusLabel task={item.task} />;
              }
            }
          ]}
          isLoading={isLoading}
          dataList={data}
          page={page}
          rowsPerPage={limit}
          onChangePage={(page: number) => setPage(page)}
          onChangeRowsPerPage={(limit: number) => setLimit(limit)}
        />
        {error && <Alert severity='error'>{error?.message}</Alert>}
      </DialogContent>
      <DialogActions>
        <Button onClick={onCancel}>Cancel</Button>
        <SelectDialogSubmitButton onSubmit={onSubmit}>
          Assign
        </SelectDialogSubmitButton>
      </DialogActions>
    </SelectDialog>
  );
};

export default AssignDocumentDialog;
